/**
 * @file        src/pages/enquiry/GroupRequestDetailModal.jsx
 * @description One group booking request in full — who to call, the itinerary
 *              they asked to be quoted, and the pipeline state the team works
 *              it through.
 *
 *              Fed from the LIST ROW, not a second fetch: /api/group-requests
 *              returns every field including the parsed itinerary, so opening a
 *              lead costs no request. Saving returns the refreshed row, which
 *              is what the list then invalidates on.
 *
 *              THE ITINERARY IS PRINTED, NOT SUMMARISED. A group quote is
 *              answered off the legs — airline, flight number, date, fare type
 *              — and those are precisely what the route columns cannot hold, so
 *              a request whose legs we drop is one somebody has to go and find
 *              in their mailbox. A leg the storefront could not name renders an
 *              em dash rather than being skipped: "we do not know" and "there
 *              is no such leg" are different answers.
 * @author      Kinetika Labs
 * @created     2026-09-25
 * @changelog
 *   - 2026-09-25 Claude — Initial (Query Management → Group Booking Requests).
 */
import { useState } from 'react';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import { setGroupRequestStatus } from '../../api/groupRequests';
import { inr, formatDate, formatDateTime } from '../../lib/format';
import { toast } from '../../lib/toast';
import { STATUS_OPTIONS, STATUS_LABEL, STATUS_TONE, TRIP_LABEL, CUSTOMER_LABEL, paxLine } from './groupRequestMeta';

function Field({ label, children }) {
  return (
    <div><span className="muted">{label}</span><div>{children || <span className="muted">—</span>}</div></div>
  );
}

export default function GroupRequestDetailModal({ row, brandLabel, onClose }) {
  const qc = useQueryClient();
  const [status, setStatus] = useState(row?.status || 'new');
  const [note, setNote] = useState(row?.adminNote || '');

  const mut = useMutation({
    mutationFn: () => setGroupRequestStatus(row.id, { status, note }),
    onSuccess: () => {
      toast.ok('Saved', 'Request updated.');
      qc.invalidateQueries({ queryKey: ['group-requests'] });
      onClose();
    },
    onError: (e) => toast.err('Could not save', e.response?.data?.error?.message || 'Please try again.'),
  });

  if (!row) return null;
  const legs = row.itinerary?.legs || [];

  return (
    <div className="lk-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="lk-modal panel glass" style={{ maxWidth: 780, width: '96%' }}>
        <div className="lk-modal-head">
          <h3>
            Group request #{row.id}
            {' · '}
            {row.origin || '—'} → {row.destination || '—'}
          </h3>
          <button className="ib" aria-label="Close" onClick={onClose}><Icon name="x" /></button>
        </div>

        <div className="det-body">
          <h4 className="det-h">Who to call back</h4>
          <div className="det-grid">
            <Field label="Name">{row.name}</Field>
            <Field label="Customer">{CUSTOMER_LABEL[row.customerType] || row.customerType}</Field>
            <Field label="Mobile">{row.mobile ? <a href={`tel:${row.mobile}`}>{row.mobile}</a> : null}</Field>
            <Field label="Email">{row.email ? <a href={`mailto:${row.email}`}>{row.email}</a> : null}</Field>
            <Field label="Brand">{brandLabel(row.brand)}</Field>
            <Field label="Received">{formatDateTime(row.createdAt)}</Field>
          </div>

          <h4 className="det-h">What they asked for</h4>
          <div className="det-grid">
            <Field label="Trip">{TRIP_LABEL[row.tripType] || row.tripType}</Field>
            <Field label="Passengers">{paxLine(row.pax)}</Field>
            <Field label="Travel date">{row.travelDate ? formatDate(row.travelDate) : null}</Field>
            {/* The CUSTOMER's figure, not a price we have quoted — the screen
                says so, because a budget sitting in a money column beside real
                amounts reads as one. */}
            <Field label="Their budget">{row.budget ? inr(row.budget) : null}</Field>
            <Field label="Fare type">{row.itinerary?.fareType}</Field>
            <Field label="Estimated total (per person)">
              {row.itinerary?.totalPrice ? inr(row.itinerary.totalPrice) : null}
            </Field>
          </div>

          <h4 className="det-h">Itinerary</h4>
          {legs.length === 0 ? (
            // No flight was chosen — the customer opened the form from the
            // search rather than off a result card. Saying so beats an empty
            // table, which reads as a broken document.
            <p className="muted">No flight was selected — quote from the route and dates above.</p>
          ) : (
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>Leg</th><th>Flight</th><th>Route</th><th>Date</th><th>Fare</th><th className="doc-num">Price</th>
                  </tr>
                </thead>
                <tbody>
                  {legs.map((l) => (
                    <tr key={l.index}>
                      <td>{legs.length > 1 ? `Leg ${l.index}` : 'Flight'}</td>
                      <td>
                        <b>{l.airline || '—'}</b>
                        {l.flightNo ? <><br /><span className="muted mono">{l.flightNo}</span></> : null}
                      </td>
                      <td className="mono">{l.from || '—'} → {l.to || '—'}</td>
                      <td>{l.date ? formatDate(l.date) : '—'}</td>
                      <td>{l.fareType || '—'}</td>
                      <td className="doc-num">{l.price ? inr(l.price) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {row.remarks ? (
            <>
              <h4 className="det-h">Their remarks</h4>
              <p style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row.remarks}</p>
            </>
          ) : null}

          <h4 className="det-h">Working it</h4>
          <div className="det-grid">
            <Field label="Current status">
              <Chip tone={STATUS_TONE[row.status]}>{STATUS_LABEL[row.status] || row.status}</Chip>
            </Field>
            <Field label="Last handled">{row.handledAt ? formatDateTime(row.handledAt) : null}</Field>
            {/* Whether the ops notification actually went out. A "Not emailed"
                here is a lead NOBODY was told about — the stored row is the only
                reason it still exists, so it has to be visible. */}
            <Field label="Notification">
              <Chip tone={row.notified ? 'ok' : 'warn'}>{row.notified ? 'Emailed' : 'Not emailed'}</Chip>
            </Field>
          </div>
          <div className="field">
            <label>Status</label>
            <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} />
          </div>
          <div className="field">
            <label>Internal note — where has this got to?</label>
            <textarea
              value={note}
              maxLength={1000}
              placeholder="e.g. quoted ₹4,20,000 for 18 pax on 12 Oct · waiting on the airline's group desk · customer chose another agency"
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
          <div className="form-actions">
            <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
            <button type="button" className="btn primary" disabled={mut.isPending} onClick={() => mut.mutate()}>
              {mut.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
